import { getAnalytics } from 'src/lib/analytics/analytics.service';
import { PERF_EVENTS, type tPerfBlockingScript, type tSlowInteractionProps } from 'src/hooks/analytics/perf/perfAnalytics.constants';
import { getPerfSampling } from './perfSampling';
import { getPerfInteractionCount, recordPerfInteraction } from './perfCounters.store';
import { getPerfRouteAt, readPerfInteractionContext } from './perfContext';

const FRAME_LIMIT = 60;
const FRAME_AGE_MS = 5000;
const SLOW_THRESHOLD_MS = 500;
const REPORT_INTERVAL_MS = 60000;
const INTERACTION_LIMIT = 2048;
const PENDING_LIMIT = 32;
const SIBLING_SCAN_LIMIT = 64;
type tScriptEntry = {
  executionStart: number; duration: number; invokerType?: string;
  sourceURL?: string; sourceFunctionName?: string; invoker?: string;
  forcedStyleAndLayoutDuration?: number;
};
type tScript = { start: number; end: number; properties: tPerfBlockingScript };
type tScriptCandidate = { raw: tScriptEntry; start: number; duration: number };
// The pinned TypeScript DOM declarations predate these Event Timing fields.
type tInteractionEntry = PerformanceEventTiming & { interactionId: number };
type tFrame = { start: number; end: number; scripts: tScript[] };
type tPending = { start: number; end: number; properties: tSlowInteractionProps; context?: Record<string, unknown> };

const safeScript = (candidate: tScriptCandidate): tScript => {
  const { raw: script, start, duration } = candidate;
  const { sourceURL: rawURL, invoker: rawInvoker, invokerType, sourceFunctionName, forcedStyleAndLayoutDuration } = script;
  let sourceURL: string | undefined;
  try {
    const parsed = rawURL ? new URL(rawURL) : undefined;
    if (parsed && (parsed.protocol === 'https:' || parsed.protocol === 'http:')) {
      const appAsset = parsed.origin === window.location.origin && /^\/assets\/[\w.-]+-[\w-]{8}\.js$/.test(parsed.pathname);
      const posthogAsset = parsed.origin === 'https://us-assets.i.posthog.com' && /^\/static\/(?:posthog-recorder|recorder|web-vitals)\.js$/.test(parsed.pathname);
      // Other paths can contain lead IDs or tenant tokens. Keep only their
      // public origin; full paths are useful only for known static assets.
      sourceURL = parsed.origin + (appAsset || posthogAsset ? parsed.pathname : '');
    }
  } catch { /* Inline and opaque script locations have no public source URL. */ }
  const invoker = rawInvoker?.replace(/\[[^\]]*\]/g, '').replace(/#[^\s.[(]+/g, '');
  const properties: tPerfBlockingScript = { duration };
  if (sourceURL) properties.sourceURL = sourceURL;
  if (invokerType && /^[a-z-]{1,40}$/.test(invokerType)) properties.invokerType = invokerType;
  if (sourceFunctionName && /^[\w.$<> ]{1,120}$/.test(sourceFunctionName)) properties.sourceFunctionName = sourceFunctionName;
  if (invoker && /^[A-Za-z]+(?:\.(?:on[a-z]+|requestAnimationFrame|setTimeout|setInterval))?$/.test(invoker)) properties.invoker = invoker;
  if (Number.isFinite(forcedStyleAndLayoutDuration)) properties.forcedStyleAndLayoutDuration = forcedStyleAndLayoutDuration;
  return {
    start, end: start + duration, properties,
  };
};

const selectorFor = (target: EventTarget | null): string => {
  const parts: string[] = [];
  let element = target instanceof Element ? target : null;
  while (element && parts.length < 3) {
    const tag = element.tagName.toLowerCase();
    if (!/^[a-z]+$/.test(tag)) break;
    let position = 1;
    let sibling = element.previousElementSibling;
    let scanned = 0;
    while (sibling && scanned < SIBLING_SCAN_LIMIT) {
      if (sibling.tagName === element.tagName) position += 1;
      sibling = sibling.previousElementSibling;
      scanned += 1;
    }
    // A truncated scan cannot establish an ordinal; use the tag alone.
    parts.unshift(sibling ? tag : `${tag}:nth-of-type(${position})`);
    element = element.parentElement;
  }
  return parts.join(' > ');
};

export const startInteractionObserver = (): (() => void) => {
  const sampling = getPerfSampling();
  if (!sampling.observe || typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes?.includes('event')) return () => {};
  const frames: tFrame[] = [];
  const recent = new Map<number, { duration: number; slowConsidered: boolean }>();
  const pending = new Map<number, tPending>();
  let lastAcceptedAt = -Infinity;
  let suppressed = 0;
  let frameObserver: PerformanceObserver | undefined;

  const attribute = (row: tPending, frame: tFrame): void => {
    if (frame.start >= row.end || frame.end <= row.start) return;
    for (const script of frame.scripts) {
      if (script.start < row.end && script.end > row.start &&
          script.properties.duration > (row.properties.blocking_script?.duration ?? -1)) {
        row.properties.blocking_script = script.properties;
      }
    }
  };

  const send = (transport?: 'sendBeacon'): void => {
    const rows = [...pending.values()].sort((left, right) => left.start - right.start);
    pending.clear();
    for (const row of rows) {
      try {
        const analytics = getAnalytics();
        const workspaceId = analytics.getRegisteredProperty('workspace_id');
        analytics.track({
          event: PERF_EVENTS.SLOW_INTERACTION,
          properties: { ...row.properties, ...row.context, ...(typeof workspaceId === 'string' ? { workspace_id: workspaceId } : {}) },
          ...(transport ? { transport } : {}),
        });
      } catch { /* Instrumentation must not interrupt navigation. */ }
    }
  };

  const onEvents = (entries: PerformanceEntry[], transport?: 'sendBeacon'): void => {
    for (const raw of entries) {
      try {
        const entry = raw as tInteractionEntry;
        const id = entry.interactionId;
        if (!id || !Number.isFinite(entry.duration) || !Number.isFinite(entry.startTime)) continue;
        recordPerfInteraction(id, entry.duration);
        if (!sampling.slowInteractions || entry.duration < SLOW_THRESHOLD_MS) continue;
        const previous = recent.get(id);
        if (previous && previous.duration >= entry.duration) continue;
        const current = { duration: entry.duration, slowConsidered: previous?.slowConsidered ?? false };
        recent.set(id, current);
        if (recent.size > INTERACTION_LIMIT) {
          const oldest = recent.keys().next().value;
          if (oldest !== undefined) recent.delete(oldest);
        }
        const route = getPerfRouteAt(entry.startTime);
        if (!route) continue;
        const queued = pending.get(id);
        if (current.slowConsidered && !queued) continue;
        if (!current.slowConsidered) {
          current.slowConsidered = true;
          if (entry.startTime - lastAcceptedAt < REPORT_INTERVAL_MS) { suppressed += 1; continue; }
          lastAcceptedAt = entry.startTime;
        }
        const inputDelay = Math.min(entry.duration, Math.max(0, entry.processingStart - entry.startTime));
        const processing = Math.min(entry.duration - inputDelay, Math.max(0, entry.processingEnd - entry.processingStart));
        const row: tPending = {
          start: entry.startTime, end: entry.startTime + entry.duration,
          context: queued?.context ?? readPerfInteractionContext(entry.startTime),
          properties: {
            interaction_id: id, interaction_type: entry.name,
            interaction_start_time_ms: entry.startTime, perf_interactions_total: getPerfInteractionCount(),
            duration_ms: entry.duration, input_delay_ms: inputDelay,
            processing_duration_ms: processing, presentation_delay_ms: entry.duration - inputDelay - processing,
            target_selector: selectorFor(entry.target), route,
            perf_sample_rate: sampling.sampleRate,
            suppressed_interactions: queued?.properties.suppressed_interactions ?? suppressed,
          } satisfies tSlowInteractionProps,
        };
        if (!queued) suppressed = 0;
        for (const frame of frames) attribute(row, frame);
        pending.set(id, row);
        if (pending.size >= PENDING_LIMIT) send(transport);
      } catch { /* One unsupported entry must not discard the rest of the batch. */ }
    }
  };

  const onFrames = (entries: PerformanceEntry[]): void => {
    for (const entry of entries) {
      try {
        if (!Number.isFinite(entry.startTime) || !Number.isFinite(entry.duration)) continue;
        const frame: tFrame = { start: entry.startTime, end: entry.startTime + entry.duration, scripts: [] };
        const candidates: tScriptCandidate[] = [];
        for (const raw of (entry as PerformanceEntry & { scripts?: tScriptEntry[] }).scripts ?? []) {
          const start = raw.executionStart;
          const duration = raw.duration;
          if (!Number.isFinite(start) || !Number.isFinite(duration) || duration < 0) continue;
          if (candidates.length === 5 && duration <= candidates[4].duration) continue;
          const position = candidates.findIndex(candidate => duration > candidate.duration);
          const candidate = { raw, start, duration };
          if (position === -1) candidates.push(candidate);
          else candidates.splice(position, 0, candidate);
          if (candidates.length > 5) candidates.pop();
        }
        // Raw browser entries live only in this callback. Sanitize just the
        // five retained scripts; the ring never holds raw entries or URLs.
        frame.scripts = candidates.map(safeScript);
        for (const row of pending.values()) attribute(row, frame);
        frames.push(frame);
        const cutoff = performance.now() - FRAME_AGE_MS;
        while (frames.length > FRAME_LIMIT || (frames[0] && frames[0].end < cutoff)) frames.shift();
      } catch { /* Raw browser entries are never retained on failure. */ }
    }
  };

  const eventObserver = new PerformanceObserver((list) => onEvents(list.getEntries()));
  try {
    const options: PerformanceObserverInit & { durationThreshold: number } = { type: 'event', buffered: true, durationThreshold: 16 };
    eventObserver.observe(options);
  } catch {
    eventObserver.disconnect();
    return () => {};
  }
  if (PerformanceObserver.supportedEntryTypes.includes('long-animation-frame')) {
    try {
      frameObserver = new PerformanceObserver((list) => onFrames(list.getEntries()));
      frameObserver.observe({ type: 'long-animation-frame', buffered: true });
    } catch { frameObserver?.disconnect(); frameObserver = undefined; }
  }
  const report = (transport?: 'sendBeacon'): void => {
    try {
      if (frameObserver) onFrames(frameObserver.takeRecords());
      onEvents(eventObserver.takeRecords(), transport);
      send(transport);
    } catch { /* Exit reporting must never cancel a page transition. */ }
  };
  // hidden commonly precedes pagehide. Deliver immediately before clearing the
  // rows, because PostHog may already have drained its ordinary capture queue.
  const onHidden = () => { if (document.visibilityState === 'hidden') report('sendBeacon'); };
  const onPageHide = () => report('sendBeacon');
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    eventObserver.disconnect();
    frameObserver?.disconnect();
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onPageHide);
    pending.clear(); frames.length = 0; recent.clear();
  };
};
